'use client';

import { useBoolean } from '@insta-club/hooks';
import { CloseBtn, Dialog, RadioButton } from '@insta-club/ui';
import { cn } from '@insta-club/utilities';
import Image from 'next/image';
import { memo } from 'react';
import { useFormContext } from 'react-hook-form';
import VideoStyleGuide from './video-style-guide';

interface VideoStyle {
    id: number;
    title: string;
    thumbnail: string;
    postType: {
        id: number;
        name: string;
        type: string;
        subject: string;
        full_name: string;
    };
    video: {
        url: string;
        hls: string;
    };
}

interface VideoStyleItemProps {
    item: VideoStyle;
}

function VideoStyleItem({ item }: VideoStyleItemProps) {
    const { register, watch, setValue, getValues } = useFormContext();
    const openGuide = useBoolean();

    const selectedVideoStyleId = watch('video_style_id');
    const isSelected = selectedVideoStyleId === String(item.id);

    const handleSelect = () => {
        setValue('video_style_id', String(item.id));
    };

    const handlePreview = (e: React.MouseEvent) => {
        e.stopPropagation();
        openGuide.onTrue();
    };

    return (
        <>
            <div
                onClick={handleSelect}
                className={cn(
                    'bg-primary-50 flex cursor-pointer items-center justify-between rounded-xl p-1 transition-colors',
                    {
                        'bg-primary-500': isSelected
                    }
                )}
            >
                <div className='flex items-center gap-2.5'>
                    {/* Thumbnail with Play Button */}
                    <div onClick={handlePreview} className='relative h-[76px] w-[61px] overflow-hidden rounded-xl'>
                        <Image
                            src={item.thumbnail ?? '/img-not-found-placeholder.jpg'}
                            alt={item.title}
                            width={61}
                            height={76}
                            className='h-full w-full object-cover'
                        />

                        {/* Overlay */}
                        <div className='absolute inset-0 bg-black/30 transition-opacity duration-300 hover:bg-black/40' />

                        {/* Play Button */}
                        <button
                            type='button'
                            className='absolute inset-0 flex items-center justify-center text-white'
                            aria-label='پیش‌نمایش ویدیو'
                        >
                            <svg
                                width={20}
                                height={20}
                                viewBox='0 0 20 20'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <g opacity={0.76}>
                                    <rect width={20} height={20} rx={10} fill='#fff' />
                                    <path
                                        d='M8.388 13.621c-.308 0-.6-.074-.858-.222-.609-.351-.944-1.066-.944-2.006V8.611c0-.944.335-1.655.944-2.006.609-.351 1.393-.285 2.212.188l2.408 1.389c.816.472 1.268 1.12 1.268 1.822s-.449 1.35-1.268 1.822l-2.408 1.39c-.472.269-.932.405-1.354.405z'
                                        fill='#374151'
                                    />
                                </g>
                            </svg>
                        </button>
                    </div>

                    {/* Title */}
                    <h5
                        className={cn('text-sm font-normal text-gray-600', {
                            'font-semibold text-white': isSelected
                        })}
                    >
                        {item.title}
                    </h5>
                </div>

                {/* Radio Button */}
                <RadioButton
                    label=''
                    value={item.id}
                    color='light'
                    controlClassName={cn({
                        'border-[4px]': isSelected
                    })}
                    checked={getValues('video_style_id') === item.id}
                    {...register('video_style_id')}
                />
            </div>

            {/* Preview Dialog */}
            <Dialog open={openGuide.value} onClose={openGuide.onFalse}>
                <div className='absolute top-4 left-4 z-50'>
                    <CloseBtn onClick={openGuide.onFalse} />
                </div>
                <VideoStyleGuide item={item} />
            </Dialog>
        </>
    );
}

export default memo(VideoStyleItem);

